import * as React from 'react';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';

interface UsePushToTalkOptions {
  /** Called with the recording on touchend (after a non-cancelled push-to-talk gesture). */
  onSendAudio?: (blob: Blob, waveform: number[], duration: number) => void;
  /** When true, push-to-talk and clicks are no-ops (e.g. while loading or sending). */
  disabled?: boolean;
  /** Pixels of left-slide before the gesture cancels recording. Default 80. */
  slideCancelThreshold?: number;
}

interface UsePushToTalkReturn {
  isRecording: boolean;
  recordingTime: number;
  audioData: number[];
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<{ blob: Blob; duration: number } | null>;
  cancelRecording: () => void;
  /** True while a push-to-talk gesture is active (touch held). */
  isPushToTalk: boolean;
  /**
   * Attach to `VoiceButton`'s `onTouchStart`. Begins recording, captures the
   * touch x-coord, then a window-level touchmove/touchend pair drives the rest.
   */
  handleTouchStart: (e: TouchEvent) => Promise<void>;
}

/**
 * Voice-recorder + push-to-talk gesture wrapper used by chat inputs.
 * Owns the recorder, plus the touch lifecycle: holding the voice button records,
 * releasing sends, and sliding the finger left past `slideCancelThreshold` cancels.
 * `audioData` is mirrored to a ref so the touchend listener (which closes over a
 * stale render) reads the latest waveform when committing the recording.
 */
export function usePushToTalk(options: UsePushToTalkOptions = {}): UsePushToTalkReturn {
  const { onSendAudio, disabled = false, slideCancelThreshold = 80 } = options;

  const { isRecording, recordingTime, audioData, startRecording, stopRecording, cancelRecording } =
    useVoiceRecorder();

  const [isPushToTalk, setIsPushToTalk] = React.useState(false);
  const touchStartX = React.useRef<number | null>(null);

  const audioDataRef = React.useRef<number[]>([]);
  React.useEffect(() => {
    audioDataRef.current = audioData;
  }, [audioData]);

  const handleTouchStart = React.useCallback(
    async (e: TouchEvent) => {
      e.preventDefault();
      if (disabled) return;

      const touch = e.touches && e.touches.length > 0 ? e.touches[0] : null;
      if (!touch) return;

      setIsPushToTalk(true);
      touchStartX.current = touch.clientX;
      await startRecording();
    },
    [disabled, startRecording]
  );

  React.useEffect(() => {
    if (!isPushToTalk) return;

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartX.current === null) return;
      const touch = e.touches.item(0) || e.changedTouches.item(0);
      if (!touch) return;
      const diff = touchStartX.current - touch.clientX;
      if (diff > slideCancelThreshold) {
        cancelRecording();
        setIsPushToTalk(false);
        touchStartX.current = null;
      }
    };

    const handleTouchEnd = async () => {
      if (touchStartX.current !== null) {
        const result = await stopRecording();
        if (result && onSendAudio) {
          onSendAudio(result.blob, audioDataRef.current, result.duration);
        }
      }
      setIsPushToTalk(false);
      touchStartX.current = null;
    };

    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isPushToTalk, cancelRecording, stopRecording, onSendAudio, slideCancelThreshold]);

  return {
    isRecording,
    recordingTime,
    audioData,
    startRecording,
    stopRecording,
    cancelRecording,
    isPushToTalk,
    handleTouchStart,
  };
}
